import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/axios';
import { useNavigate } from 'react-router-dom';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import SessionWarningDialog from '../components/SessionWarningDialog';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [userRole, setUserRole] = useState(null);
  const [fullname, setFullname] = useState(null);
  const [lastlogin, setLastlogin] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [hospitalId, setHospitalId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const navigate = useNavigate();

  const clearAuth = useCallback(() => {
    setIsAuthenticated(false);
    setUserRole(null);
    setFullname(null);
    setLastlogin(null);
    setPermissions([]);
    setHospitalId(null);
  }, []);

  const handleSessionLogout = useCallback(() => {
    clearAuth();
    navigate('/');
  }, [clearAuth, navigate]);

  const verifyAuth = async () => {
    try {
      const response = await api.get('/auth/verify');
      if (response.data.status === 'authenticated') {
        setIsAuthenticated(true);
        setUserRole(response.data.role);
        setFullname(response.data.fullname);
        setLastlogin(response.data.lastlogin);
        setPermissions(response.data.permissions || []);
        setHospitalId(response.data.hospital_id);
      } else {
        clearAuth();
      }
    } catch (error) {
      console.error("Auth verification failed:", error);
      clearAuth();
    } finally {
      setAuthChecked(true);
    }
  };

  useEffect(() => {
    verifyAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Session timeout warning ────────────────────────────────────────────────
  const { showWarning, countdown, stayLoggedIn, doLogout } = useSessionTimeout({
    isAuthenticated,
    onLogout: handleSessionLogout,
  });

  return (
    <AuthContext.Provider value={{
      userRole,
      fullname,
      lastlogin,
      permissions,
      hospitalId,
      isAuthenticated,
      authChecked,
      verifyAuth,
      setUserRole,
      setFullname,
      setLastlogin,
      setPermissions,
      setHospitalId,
      setIsAuthenticated
    }}>
      {children}

      {/* Global session expiry warning — shown on top of everything */}
      <SessionWarningDialog
        open={showWarning}
        countdown={countdown}
        onStayLoggedIn={stayLoggedIn}
        onLogout={doLogout}
      />
    </AuthContext.Provider>
  );
};
